// Activity rows for the WO↔container link (ContainerToWorkOrder Envers revisions):
// a DELETE revision = container removed from the WO, an INSERT = added/restored. MOD revisions
// carry no user-facing change on the join row, so they're skipped.
//
// Payload:
//  - containersById: { [containerId]: [revisions] } from GET /api/v1/versions/work-order/{id}/containers
//    (tolerates a flat array too). Revision fields: containerId, actionType, rev, revType,
//    updatedAt, lastModifiedBy (+ serialNumber snapshot when the BE provides it).
//  - workOrderContainers: the WO containers list, used to label rows the revision itself can't.
import { keyBy, sortBy } from 'lodash'

import { getMeasureUnit } from 'utils/get-measure-unit'
import { get } from 'utils/lodash'

export const resolveContainerLabel = (revision, containerById) => {
  const container = get(containerById, get(revision, 'containerId', ''), undefined)
  const serial = get(revision, 'serialNumber') || get(container, 'serialNumber') || get(container, 'barCode')
  const measure = get(container, 'measure')
  const sizeFromMeasure = measure ? `${get(measure, 'volume', '')} ${getMeasureUnit(get(measure, 'unit', ''))}`.trim() : ''
  const size = sizeFromMeasure || get(container, 'containerType.name', '')
  return [size, serial].filter(Boolean).join(' - ') || 'Container'
}

// Envers meta arrives as a flat `lastModifiedBy` on the join revision; shape it like the
// updatedByUser object extractUserMeta expects so the batch header shows the right author.
const toVersionMeta = revision => ({
  ...revision,
  updatedByUser: get(revision, 'updatedByUser') || (revision?.lastModifiedBy ? { username: revision.lastModifiedBy } : undefined),
})

export default function workOrderContainerLinkBuilder(builder, payload = {}) {
  const { containersById, workOrderContainers = [] } = payload
  if (!containersById) return

  const containerById = keyBy(workOrderContainers, 'id')
  const revisionLists = Array.isArray(containersById) ? [containersById] : Object.values(containersById)

  revisionLists.forEach(revisions => {
    if (!Array.isArray(revisions) || revisions.length === 0) return
    const sorted = sortBy(revisions, revision => Number(revision?.rev ?? 0))

    sorted.forEach(revision => {
      const revType = String(revision?.revType || revision?.type || '').toUpperCase()
      const isRemoved = revType === 'DELETE' || revType === 'DEL'
      const isAdded = revType === 'INSERT' || revType === 'ADD'
      if (!isRemoved && !isAdded) return

      const label = resolveContainerLabel(revision, containerById)
      builder.addCustomChange(revision?.rev, toVersionMeta(revision), {
        field: 'Container',
        fieldKey: isRemoved ? 'workOrderContainerRemoved' : 'workOrderContainerAdded',
        oldValue: isRemoved ? label : undefined,
        newValue: isRemoved ? undefined : label,
        changeType: isRemoved ? 'Deleted' : 'Added',
      })
    })
  })
}
